import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { MeetingFeedbackModal } from '@/components/modals/MeetingFeedbackModal';
import { ModalsManager } from '@/components/modals/modalsManager';
import {
  useVoteEntity,
  VoteDataDirection,
  type VoteDataDirectionType,
} from '@/query/vote';

export type ActionCardVoteChoice = 'good' | 'bad';

// An internal user's good or bad on a card, which BTS lists with the card's routine run, chats and watcher run
export function useActionCardVote(cardId: string) {
  const { mutateAsync: voteEntity } = useVoteEntity();
  // Keyed by card, so a menu that moves to another card shows no vote
  const [vote, setVote] = useState<{
    cardId: string;
    choice: ActionCardVoteChoice;
  } | null>(null);
  const voted = vote !== null && vote.cardId === cardId ? vote.choice : null;

  const openFeedback = useCallback(
    (initialDirection: VoteDataDirectionType) => {
      ModalsManager.show(MeetingFeedbackModal, {
        initialDirection,
        title: 'Rate this card',
        description:
          'Say what was good or wrong about it. BTS shows your vote with the routine run that made the card, its chats and the watcher.',
        onSubmit: async (
          direction: VoteDataDirectionType,
          reasoningInput: string
        ) => {
          await voteEntity({
            entity_type: 'action_card',
            entity_id: cardId,
            direction,
            reasoning: reasoningInput.trim() || null,
          });
          setVote({
            cardId,
            choice: direction === VoteDataDirection.UP ? 'good' : 'bad',
          });
          toast.success('Thanks for the feedback');
        },
      });
    },
    [cardId, voteEntity]
  );

  const rateGood = useCallback(
    () => openFeedback(VoteDataDirection.UP),
    [openFeedback]
  );

  const rateBad = useCallback(
    () => openFeedback(VoteDataDirection.DOWN),
    [openFeedback]
  );

  return { voted, rateGood, rateBad };
}
